import React, { useEffect, useState } from 'react';
import { FormRenderProps } from '@progress/kendo-react-form';
import styles from "../../../Tab.module.scss";
import { useRouteLoaderData } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useFormOutletContext } from './CustomerInfo';
import { IAppSettings, IConnectedService, ICustomer, ILookupValue } from '../../../../model';
import { selectAppSettings } from '../../../../features';
import { industrySectorsService } from '../../../../services/api/industry-sectors';
import { AddressDetails } from '../../../../components/customer/Details/AddressDetails/AddressDetails';
import CustomerDetailsGeneral from '../../../../components/customer/Details/General/CustomerDetailsGeneral';
import CustomerDetailsContacts from '../../../../components/customer/Details/Contacts/CustomerDetailsContacts';
import { CustomerConnectedServices } from '../../../../components/customer/Details/CustomerConnectedServices/CustomerConnectedServices';
import CustomerDetailsLinks from '../../../../components/customer/Details/Links/CustomerDetailsLinks';

const createInnerNavLinks = (isNew: boolean | undefined) => {
    const innerNavLinks = ['General', 'Address', 'Contacts', 'Services', 'Links'];
    const innerNavLinksForNewCustomer = ['General', 'Address', 'Services'];
    return isNew ? innerNavLinksForNewCustomer : innerNavLinks;
}

const CustomerDetailsTab = () => {
  const customer = useRouteLoaderData('customer-info') as ICustomer;
  const { formRenderProps, edit, isNew, onInnerTabChange } = useFormOutletContext();
  const [activeInnerTab, setActiveInnerTab] = useState('General');
  const appSettings: IAppSettings | null = useSelector(selectAppSettings);

  const [industrySectors, setIndustrySectors] = useState<ILookupValue[]>([]);
  const [connectedServices, setConnectedServices] = useState<IConnectedService[]>([]);
  const innerNavLinks = createInnerNavLinks(isNew);
  const { hasSnapshotAccess } = useFormOutletContext();

  useEffect(() => {
    if (!edit) {
      return;
    }
    industrySectorsService.getLookupValues().then((x) => setIndustrySectors(x));
  }, [customer, edit]);

  const handleTabClick = (tabName: string) => {
    setActiveInnerTab(tabName);
    onInnerTabChange(tabName);
  };

  const renderInnerContent = (formRenderProps: FormRenderProps) => {
    switch (activeInnerTab) {
      case 'General':
        return (
          <CustomerDetailsGeneral
            edit={edit}
            hasSnapshotAccess={hasSnapshotAccess}
            isNew={isNew}
            customer={customer}
            appSettings={appSettings}
            industrySectors={industrySectors}
            formRenderProps={formRenderProps}
            {...formRenderProps}
          />
        );
      case 'Address':
        return <AddressDetails edit={edit} geometry={customer?.address?.geometry} {...formRenderProps} />;
      case 'Contacts':
        return <CustomerDetailsContacts edit={edit} isNew={isNew} formRenderProps={formRenderProps} />;
      case 'Services':
        return (
          <CustomerConnectedServices
            edit={edit}
            customerId={customer.id}
            onConnectedServicesChange={(connectedServices: IConnectedService[]) => {
              setConnectedServices(connectedServices);
            }}
            {...formRenderProps}
          />
        );
      case 'Links':
        return <CustomerDetailsLinks isNew={isNew} edit={edit} formRenderProps={formRenderProps} />;
      default:
        return <div>Page detail for {activeInnerTab}</div>;
    }
  };

  return (
    <div className={styles.detailsLayout}>
      <nav className={styles.innerSidebar}>
        <ul>
          {innerNavLinks.map((tabName) => (
            <li key={tabName}>
              <button
                type={'button'}
                className={activeInnerTab === tabName ? styles.active : ''}
                onClick={() => handleTabClick(tabName)}
              >
                {tabName}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      <div className={styles.contentWrapper}>
        <h2 style={{ fontWeight: 400 }}>{activeInnerTab}</h2>
        {renderInnerContent(formRenderProps)}
      </div>
    </div>
  );
};

export default CustomerDetailsTab;
